import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { useAppContext } from '../../context/AppContext';
import { useDragAndDrop, isDraggingState } from '../../hooks/useDragAndDrop';
import { useCategories } from '../../hooks/useCategories';
import { Category, Marker } from '../../types';
import { getReactIconForCategory } from '../../utils/iconMigration';
import * as FaIcons from 'react-icons/fa';
import { FaPlus, FaChevronDown } from 'react-icons/fa';
import logger from '../../utils/logger';
import Modal from '../modal/Modal';
import CategoryForm from '../categories/CategoryForm';
import { useMarkers } from '../../hooks';
import { useNotifications } from '../../context/NotificationContext';
import { eventsService } from '../../services/events.service';
import useWindowSize from '../../hooks/useWindowSize';
import { useAuth } from '../../context/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useUserFilter } from '../../context/UserFilterContext';
import { softendColor } from '../../utils/markerUtils';
import { doWithRef } from '../../utils/refUtils';
import { categoriesService } from '../../services/supabase/categories.service';
import { ZonesSection } from './ZonesSection';
import { Toast } from '../common/Toast';
import { SectionErrorBoundary } from '../common/SectionErrorBoundary';
import ZoneForm from '../prospection/ZoneForm';
import type { Coordinate, ProspectionZone } from '../../types/prospectionZone';
import {
  SidebarContainer,
  SidebarContent,
  MenuContainer,
  MenuItem,
  MenuHeader,
  MenuTitle,
  MenuIcon,
  MenuContent,
  MenuInnerContent,
  CategoryList,
  AddMenuButton,
  MarkerList,
  MarkerItem,
  ToggleSidebarButton
} from './styles';
import { ListItem } from './ListItem';

// Configuration des menus pour faciliter l'ajout futur
const MENU_CONFIG = {
  categories: {
    title: '📁 Catégories',
    icon: '📁',
    defaultExpanded: true,
    addAction: 'handleAddCategory',
    addTitle: 'Ajouter une catégorie'
  },
  zones: {
    title: '🗺️ Zones',
    icon: '🗺️',
    defaultExpanded: false,
    addAction: 'handleAddZone',
    addTitle: 'Ajouter une zone'
  },
  // Futurs menus à ajouter ici :
  // reports: {
  //   title: '📊 Rapports',
  //   icon: '📊',
  //   defaultExpanded: false,
  //   addAction: 'handleAddReport',
  //   addTitle: 'Ajouter un rapport'
  // }
} as const;

type MenuName = keyof typeof MENU_CONFIG;



/**
 * Props pour le composant MarkerItem
 */
interface MarkerItemProps {
  marker: Marker;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, marker: Marker) => void;
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  onMarkerClick: (markerId: string) => void;
  showUserAvatar?: boolean; // Nouvelle prop pour afficher l'avatar de l'utilisateur
  onMarkerTouchStart?: (e: React.TouchEvent, markerId: string) => void;
  onMarkerTouchMove?: (e: React.TouchEvent) => void;
  onMarkerTouchEnd?: (e: React.TouchEvent) => void;
  isMobile?: boolean;
}

/**
 * Composant optimisé pour afficher un marqueur individuel
 */
const MemoizedMarkerItem = React.memo(({ marker, onDragStart, onDragEnd, onMarkerClick, showUserAvatar, onMarkerTouchStart, onMarkerTouchMove, onMarkerTouchEnd, isMobile }: MarkerItemProps) => {
  // Récupérer les informations de l'utilisateur associé au marqueur
  const { getUserInfo } = useUserProfile();
  const userInfo = marker.user_id ? getUserInfo(marker.user_id) : null;

  // isMobile est maintenant passé en prop
  
  // Utiliser l'état global de glissement importé
  
  // Fonctions pour animer le marqueur au survol et au clic
  const handleMouseEnter = () => {
    // Désactiver l'animation sur mobile ou pendant un glisser-déposer
    if (!isMobile && !isDraggingState.current) {
      eventsService.emit('ANIMATE_MARKER', { markerId: marker.id });
    }
  };

  const handleClick = () => {
    // Ne pas traiter le clic si on est en train de faire un glisser-déposer
    if (isDraggingState.current) {
      return;
    }
    
    // Sur mobile, ne pas animer ni ouvrir le popup automatiquement
    if (!isMobile) {
      // Émettre l'événement d'animation du marqueur
      eventsService.emit('ANIMATE_MARKER', { markerId: marker.id });
      // Émettre l'événement d'ouverture du popup du marqueur
      eventsService.emit('OPEN_MARKER_POPUP', { markerId: marker.id });
    }
    // Appeler la fonction de clic d'origine (toujours exécutée, même sur mobile)
    onMarkerClick(marker.id);
  };

  return (
    <MarkerItem
      draggable={!isMobile}
      onDragStart={(e) => onDragStart(e, marker)}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onTouchStart={(e) => {
        if (isMobile && onMarkerTouchStart) {
          e.stopPropagation(); // Empêcher la propagation vers la catégorie
          onMarkerTouchStart(e, marker.id);
        }
      }}
      onTouchMove={(e) => {
        if (isMobile && onMarkerTouchMove) {
          e.stopPropagation();
          onMarkerTouchMove(e);
        }
      }}
      onTouchEnd={(e) => {
        if (isMobile && onMarkerTouchEnd) {
          e.stopPropagation();
          onMarkerTouchEnd(e);
        }
      }}
      style={{ cursor: 'pointer', position: 'relative' }}
      className="marker-item"
    >
      {marker.address}
      
      {/* Afficher l'avatar de l'utilisateur si demandé et si les informations sont disponibles */}
      {showUserAvatar && userInfo && (
        <div
          style={{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            position: 'absolute',
            bottom: '-5px',
            right: '5px',
            transform: 'translate(0, 0)',
            border: `2px solid ${softendColor(userInfo.color || '#4285F4')}`,
            outline: '1px solid rgba(0,0,0,0.3)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 5,
            backgroundColor: softendColor(userInfo.color || '#4285F4'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            ...(userInfo.photo_url ? { backgroundImage: `url(${userInfo.photo_url})` } : {})
          }}
        >
          {!userInfo.photo_url ? userInfo.initials || 'US' : ''}
        </div>
      )}
    </MarkerItem>
  );
});

/**
 * Props pour le composant CategoryItem
 */
interface CategoryItemProps {
  category: Category;
  markers: Marker[];
  isExpanded: boolean;
  onToggle: (categoryId: string) => void;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  dragHandlers: ReturnType<typeof useDragAndDrop>;
  onMarkerClick: (markerId: string) => void;
  onCategoryDragStart: (e: React.DragEvent, categoryId: string) => void;
  onCategoryDragOver: (e: React.DragEvent, categoryId: string) => void;
  onCategoryDragLeave: () => void;
  onCategoryDrop: (e: React.DragEvent, categoryId: string) => void;
  onCategoryDragEnd: () => void;
  onCategoryTouchStart: (e: React.TouchEvent, categoryId: string) => void;
  onCategoryTouchMove: (e: React.TouchEvent) => void;
  onCategoryTouchEnd: (e: React.TouchEvent) => void;
  onMarkerTouchStart: (e: React.TouchEvent, markerId: string) => void;
  onMarkerTouchMove: (e: React.TouchEvent) => void;
  onMarkerTouchEnd: (e: React.TouchEvent) => void;
  draggedCategoryId?: string | null;
  isMobile?: boolean;
}

/**
 * Composant optimisé pour afficher une catégorie
 */
const MemoizedCategoryItem = React.memo(({ category, markers, isExpanded, onToggle, onEdit, onDelete, dragHandlers, onMarkerClick, onCategoryDragStart, onCategoryDragOver, onCategoryDragLeave, onCategoryDrop, onCategoryDragEnd, onCategoryTouchStart, onCategoryTouchMove, onCategoryTouchEnd, onMarkerTouchStart, onMarkerTouchMove, onMarkerTouchEnd, draggedCategoryId, isMobile }: CategoryItemProps) => {
  const { state, dispatch: _dispatch } = useAppContext();
  const { userProfile } = useAuth();
  const { selectedUserId } = useUserFilter();
  
  // Vérifier si l'administrateur est en mode "Tous les utilisateurs"
  const isOwner = userProfile?.is_owner || false;
  const showUserAvatars = isOwner && selectedUserId === null; // null signifie "Tous les utilisateurs"
  
  const markerPreview = useMemo(() => {
    const IconComponent = getReactIconForCategory(category.icon);
    const markerShape = category.markerShape || 'circle';
    
    if (markerShape === 'circle') {
      // Afficher le cercle avec l'icône
      return (
        <div style={{
          width: '26px',
          height: '26px',
          borderRadius: '50%',
          backgroundColor: category.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 0 0 2px white, 0 0 5px rgba(0,0,0,0.3)',
          flexShrink: 0
        }}>
          {IconComponent && <IconComponent size={14} color="#ffffff" />}
        </div>
      );
    } else {
      // Afficher l'icône directe (maison, immeuble, personne, marker)
      let DirectIconComponent;
      switch (markerShape) {
        case 'house':
          DirectIconComponent = FaIcons.FaHome;
          break;
        case 'building':
          DirectIconComponent = FaIcons.FaBuilding;
          break;
        case 'person':
          DirectIconComponent = FaIcons.FaUser;
          break;
        case 'marker':
          DirectIconComponent = FaIcons.FaMapMarkerAlt;
          break;
        default:
          DirectIconComponent = IconComponent;
      }
      
      return DirectIconComponent ? (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          flexShrink: 0
        }}>
          <DirectIconComponent 
            size={22} 
            color="white"
            style={{ 
              WebkitTextStroke: `2px ${category.color}`,
              paintOrder: 'stroke fill',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
            }}
          />
        </div>
      ) : null;
    }
  }, [category.icon, category.color, category.markerShape]);

  const isVisible = state.visibleCategories.has(category.id);
  const isDragging = draggedCategoryId === category.id;

  return (
    <ListItem
      id={category.id}
      title={category.name}
      color={category.color || '#2c3e50'}
      isVisible={isVisible}
      isExpanded={isExpanded}
      isDragging={isDragging}
      isOwner={isOwner}
      expandable={true}
      leftIcon={markerPreview}
      draggable={!isMobile}
      onDragStart={(e: React.DragEvent) => onCategoryDragStart(e as React.DragEvent<HTMLDivElement>, category.id)}
      onDragOver={(e: React.DragEvent) => {
        // Gérer le drag over pour les markers (existant)
        dragHandlers.handleDragOver(e as React.DragEvent<HTMLDivElement>);
        // Gérer le drag over pour les catégories (nouveau)
        onCategoryDragOver(e as React.DragEvent<HTMLDivElement>, category.id);
      }}
      onDragLeave={(e: React.DragEvent) => {
        dragHandlers.handleDragLeave(e as React.DragEvent<HTMLDivElement>);
        onCategoryDragLeave();
      }}
      onDrop={(e: React.DragEvent) => {
        // Vérifier si c'est un marker ou une catégorie qui est droppé
        const droppedCategoryId = e.dataTransfer.getData('categoryId');
        if (droppedCategoryId) {
          // C'est une catégorie
          onCategoryDrop(e as React.DragEvent<HTMLDivElement>, category.id);
        } else {
          // C'est un marker
          dragHandlers.handleDrop(e as React.DragEvent<HTMLDivElement>, category.id);
        }
      }}
      onDragEnd={onCategoryDragEnd as (e: React.DragEvent) => void}
      onTouchStart={(e) => {
        // Sur mobile, gérer le touch uniquement sur le header (pas sur les markers ni les boutons)
        const target = e.target as HTMLElement;
        if (target.closest('.marker-item') || target.closest('svg') || target.closest('input')) {
          return; // Ne pas gérer le touch si on est sur un marker, un bouton ou une checkbox
        }
        onCategoryTouchStart(e, category.id);
      }}
      onTouchMove={onCategoryTouchMove}
      onTouchEnd={onCategoryTouchEnd}
      className="category-item"
      data-category-id={category.id}
      onToggleVisibility={() => {
        // Logique existante de toggle visibilité pour les catégories
        const newVisibility = !isVisible;
        
        // Mettre à jour l'état local immédiatement pour une meilleure UX
        _dispatch({ type: 'TOGGLE_CATEGORY_VISIBILITY', payload: category.id });
    
        // Persister en base de données
        (async () => {
          try {
            const { categoriesService } = await import('../../services/supabase/categories.service');
            await categoriesService.updateVisibility(category.id, newVisibility);
            logger.info(`Visibilité de la catégorie ${category.name} mise à jour: ${newVisibility}`);
          } catch (error) {
            logger.error(`Erreur lors de la mise à jour de la visibilité de la catégorie ${category.name}:`, error);
            // Annuler le changement local en cas d'erreur
            _dispatch({ type: 'TOGGLE_CATEGORY_VISIBILITY', payload: category.id });
          }
        })();
      }}
      onToggleExpand={onToggle}
      onEdit={onEdit}
      onDelete={onDelete}
      itemData={category}
    >
      {/* Contenu des marqueurs */}
      <MarkerList 
        style={{ display: isExpanded ? 'block' : 'none' }}
        onDragStart={(e) => {
          // Empêcher la propagation du drag des markers vers la catégorie
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          // Empêcher la propagation du touch des markers vers la catégorie
          e.stopPropagation();
        }}
      >
        {markers.map(marker => (
          <MemoizedMarkerItem
            key={marker.id}
            marker={marker}
            onDragStart={(e, m) => dragHandlers.handleDragStart(e, m.id)}
            onDragEnd={dragHandlers.handleDragEnd}
            onMarkerClick={onMarkerClick}
            showUserAvatar={showUserAvatars}
            onMarkerTouchStart={onMarkerTouchStart}
            onMarkerTouchMove={onMarkerTouchMove}
            onMarkerTouchEnd={onMarkerTouchEnd}
            isMobile={isMobile}
          />
        ))}
      </MarkerList>
    </ListItem>
  );
});

interface SidebarProps {
  mapRef: React.MutableRefObject<mapboxgl.Map | null>;
  isCollapsed: boolean;
  onToggleSidebar: () => void;
  onAddCategoryRef?: React.MutableRefObject<(() => void) | null>;
}

/**
 * Composant principal Sidebar
 */
const Sidebar: React.FC<SidebarProps> = ({ mapRef, isCollapsed, onToggleSidebar, onAddCategoryRef }) => {
  const { state, dispatch: _dispatch } = useAppContext();
  const { categories, deleteCategory, updateCategory, addCategory } = useCategories();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>(undefined);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedMenus, setExpandedMenus] = useState<Record<MenuName, boolean>>(() => ({
    categories: MENU_CONFIG.categories.defaultExpanded,
    zones: MENU_CONFIG.zones.defaultExpanded
  }));
  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [showZoneToast, setShowZoneToast] = useState(false);
  const [drawingCoordinates, setDrawingCoordinates] = useState<Coordinate[]>([]);
  const [editingZone, setEditingZone] = useState<ProspectionZone | undefined>(undefined);
  const { isMobile } = useWindowSize();
  const { addNotification } = useNotifications();
  
  // Récupérer le profil utilisateur pour vérifier s'il est administrateur
  const { userProfile } = useUserProfile();
  const isOwner = userProfile?.is_owner || false;
  
  // dragHandlers doit être appelé directement (c'est un hook)
  // La mémorisation se fait à l'intérieur de useDragAndDrop si nécessaire
  const dragHandlers = useDragAndDrop();
  const { openMarkerPopup, forceMapRefresh } = useMarkers(mapRef);

  // Référence pour suivre si le composant est monté
  const isMountedRef = useRef(true);

  // Effet pour ajuster la sidebar au chargement initial
  useEffect(() => {
    // N'exécuter l'ajustement que si la sidebar est visible
    if (isCollapsed) return;
    
    // Fonction d'ajustement des éléments de la sidebar
    const adjustSidebarElements = () => {
      const sidebarContainer = document.querySelector('.sidebar-container');
      if (!sidebarContainer) return;
      
      // Forcer un reflow pour recalculer les dimensions
      sidebarContainer.getBoundingClientRect();
      
      // Ajuster les éléments de catégorie
      document.querySelectorAll('.category-item').forEach(item => {
        const element = item as HTMLElement;
        element.style.width = '100%';
        element.style.maxWidth = 'calc(100% - 4px)';
      });
      
      // Ajuster le bouton d'ajout de catégorie si l'utilisateur est propriétaire
      if (isOwner) {
        const addButton = document.querySelector('.add-category-button');
        if (addButton) {
          const buttonElement = addButton as HTMLElement;
          buttonElement.getBoundingClientRect(); // Forcer un reflow
          buttonElement.style.display = 'flex';
          buttonElement.style.visibility = 'visible';
          buttonElement.style.minWidth = '36px';
          buttonElement.style.minHeight = '36px';
        }
      }
    };
    
    // Exécuter l'ajustement après un court délai pour s'assurer que le DOM est chargé
    const initialAdjustmentTimeout = setTimeout(adjustSidebarElements, 50);
    
    // Exécuter un second ajustement après un délai plus long pour s'assurer que tout est rendu
    const secondAdjustmentTimeout = setTimeout(adjustSidebarElements, 300);
    
    // Nettoyer les timeouts lors du démontage du composant
    return () => {
      clearTimeout(initialAdjustmentTimeout);
      clearTimeout(secondAdjustmentTimeout);
    };
  }, [isCollapsed, isOwner]);
  
  // Effet pour s'abonner aux événements de mise à jour des marqueurs
  useEffect(() => {
    // Définir les types pour les données d'événements
    interface MarkerUpdateEventData {
      markerId: string;
      categoryId?: string;
    }

    interface MarkerCategoryChangeEventData {
      markerId: string;
      oldCategoryId?: string;
      newCategoryId: string;
    }

    // S'abonner à l'événement MARKERS_UPDATED
    const unsubscribe = eventsService.subscribe('MARKERS_UPDATED', (data: MarkerUpdateEventData) => {
      logger.debug('Sidebar a reçu un événement MARKERS_UPDATED', data);
      
      // Forcer un rafraîchissement du composant si nécessaire
      doWithRef(isMountedRef, () => {
        // Pas besoin de forcer un re-rendu car le state.markers est déjà mis à jour
        // par le dispatch dans updateMarker, et le composant se mettra à jour automatiquement
      });
    }, 'sidebar-component');

    // S'abonner à l'événement REFRESH_MAP_MARKERS
    const unsubscribeRefresh = eventsService.subscribe('REFRESH_MAP_MARKERS', (data: MarkerCategoryChangeEventData) => {
      logger.debug('Sidebar a reçu un événement REFRESH_MAP_MARKERS', data);
      // Pas besoin d'action supplémentaire ici, le composant se mettra à jour automatiquement
    }, 'sidebar-component');

    // Nettoyage lors du démontage du composant
    return () => {
      isMountedRef.current = false;
      unsubscribe();
      unsubscribeRefresh();
    };
  }, []);

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  const toggleMenu = useCallback((menuName: MenuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  }, []);

  const handleEditCategory = useCallback((category: Category) => {
    logger.info('Édition de catégorie', { categoryId: category.id });
    setEditingCategory(category);
    setIsModalOpen(true);
  }, []);

  const handleDeleteCategory = useCallback((category: Category) => {
    logger.debug(`Début de la suppression de la catégorie ${category.name} (${category.id})`);
    
    // Supprimer la catégorie dans la base de données
    deleteCategory(category.id)
      .then((success) => {
        // Vérifier si la suppression a été confirmée par l'utilisateur
        if (!success) {
          logger.debug(`Suppression de la catégorie ${category.id} annulée par l'utilisateur`);
          
          // Supprimer la notification de chargement
          addNotification({
            type: 'info',
            title: 'Suppression annulée',
            message: `La suppression de la catégorie "${category.name}" a été annulée.`,
            isActive: true
          });
          
          return; // Ne pas continuer si la suppression a été annulée
        }
        
        logger.debug(`Catégorie ${category.id} supprimée, mise à jour de l'interface...`);
        
        // Forcer le rafraîchissement immédiat de la carte en excluant explicitement la catégorie supprimée
        forceMapRefresh([category.id]);
        
        // Ne pas afficher de notification de succès
        // Juste logger l'action pour le débogage
        logger.info(`Suppression de la catégorie ${category.name} terminée avec succès`);
      })
      .catch(error => {
        logger.error(`Erreur lors de la suppression de la catégorie ${category.id}:`, error);
        
        // Notification d'erreur - conserver uniquement en cas d'erreur
        addNotification({
          type: 'warning',
          title: 'Erreur de suppression',
          message: `Erreur lors de la suppression de la catégorie "${category.name}".`,
          isActive: true
        });
      });
  }, [addNotification, deleteCategory, forceMapRefresh]);

  const handleAddCategory = useCallback(() => {
    setIsModalOpen(true);
    setEditingCategory(undefined);
  }, []);

  const handleAddZone = useCallback(() => {
    console.log('➕ Clic sur + Zones → Activation directe du mode dessin');
    // Afficher le toast de notification
    setShowZoneToast(true);
    // Ne plus ouvrir le modal, juste activer le mode dessin
    eventsService.emit('START_ZONE_DRAWING', {});
    console.log('🎨 Événement START_ZONE_DRAWING émis');
  }, []);

  // Écouter l'événement de fin de dessin pour ouvrir le modal
  useEffect(() => {
    const handleDrawingComplete = (data: any) => {
      console.log('✅ Dessin de zone terminé, ouverture du modal');
      setDrawingCoordinates(data.coordinates || []);
      setIsZoneModalOpen(true);
    };

    const unsubscribe = eventsService.subscribe('ZONE_DRAWING_COMPLETE', handleDrawingComplete, 'sidebar-zone-complete');
    
    return () => {
      unsubscribe();
    };
  }, []);

  // Écouter les mises à jour des coordonnées de dessin
  useEffect(() => {
    const handleCoordinatesUpdate = (data: any) => {
      setDrawingCoordinates(data.coordinates || []);
    };

    const unsubscribe = eventsService.subscribe('DRAWING_COORDINATES_UPDATE', handleCoordinatesUpdate);
    
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (onAddCategoryRef) {
      // Nous devons utiliser l'assignation directe ici car nous modifions la valeur de .current
      onAddCategoryRef.current = handleAddCategory;
    }
  }, [handleAddCategory, onAddCategoryRef]);

  const handleSubmitCategory = useCallback(async (categoryData: Omit<Category, 'id'>) => {
    if (editingCategory) {
      logger.debug('Mise à jour de la catégorie:', {
        categoryId: editingCategory.id,
        oldMarkerShape: editingCategory.markerShape,
        newMarkerShape: categoryData.markerShape,
        categoryData
      });
      
      const updatedCategory = await updateCategory(editingCategory.id, categoryData);
      
      logger.debug('Catégorie mise à jour:', updatedCategory);
      
      // Forcer le rafraîchissement des markers pour refléter les changements de forme/icône
      // Attendre que la mise à jour soit propagée puis rafraîchir
      setTimeout(() => {
        logger.debug('Émission de REFRESH_MAP_MARKERS pour categoryId:', editingCategory.id);
        logger.info('Rafraîchissement des markers après modification de catégorie');
        eventsService.emit('REFRESH_MAP_MARKERS', { 
          categoryId: editingCategory.id,
          reason: 'category_updated'
        });
      }, 200);
    } else {
      addCategory(categoryData);
    }
    setIsModalOpen(false);
    setEditingCategory(undefined);
  }, [editingCategory, updateCategory, addCategory]);

  const handleMarkerClick = useCallback((markerId: string) => {
    openMarkerPopup(markerId);
  }, [openMarkerPopup]);

  // État pour le drag & drop des catégories
  const [draggedCategoryId, setDraggedCategoryId] = useState<string | null>(null);
  const [dragOverCategoryId, setDragOverCategoryId] = useState<string | null>(null);
  
  // État pour le drag & drop tactile (mobile)
  const [isDraggingCategory, setIsDraggingCategory] = useState(false);
  
  // État pour le drag & drop tactile des markers
  const [draggedMarkerId, setDraggedMarkerId] = useState<string | null>(null);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  
  // Ajouter une classe CSS pour l'élément en cours de drag
  useEffect(() => {
    const categoryItems = document.querySelectorAll('.category-item');
    categoryItems.forEach(item => {
      const element = item as HTMLElement;
      if (dragOverCategoryId && element.dataset.categoryId === dragOverCategoryId) {
        element.classList.add('droppable');
      } else {
        element.classList.remove('droppable');
      }
    });
  }, [dragOverCategoryId]);

  // Handlers pour le drag & drop des catégories
  const handleCategoryDragStart = useCallback((e: React.DragEvent, categoryId: string) => {
    // Empêcher le drag des catégories sur mobile (on utilisera touch à la place)
    if (isMobile) {
      e.preventDefault();
      return;
    }
    
    setDraggedCategoryId(categoryId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('categoryId', categoryId);
    
    // Créer une image transparente pour éviter la ghost image par défaut
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
  }, [isMobile]);

  const handleCategoryDragOver = useCallback((e: React.DragEvent, categoryId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategoryId(categoryId);
  }, []);

  const handleCategoryDragLeave = useCallback(() => {
    setDragOverCategoryId(null);
  }, []);

  const handleCategoryDrop = useCallback(async (e: React.DragEvent, targetCategoryId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedCategoryId || draggedCategoryId === targetCategoryId) {
      setDraggedCategoryId(null);
      setDragOverCategoryId(null);
      return;
    }

    const categories = [...state.categories];
    const draggedIndex = categories.findIndex(c => c.id === draggedCategoryId);
    const targetIndex = categories.findIndex(c => c.id === targetCategoryId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedCategoryId(null);
      setDragOverCategoryId(null);
      return;
    }

    // Réorganiser les catégories
    const [draggedCategory] = categories.splice(draggedIndex, 1);
    categories.splice(targetIndex, 0, draggedCategory);

    // Mettre à jour les displayOrder
    const updates = categories.map((cat, index) => ({
      id: cat.id,
      displayOrder: index
    }));

    // Mettre à jour l'état local immédiatement pour une UX fluide
    const updatedCategories = categories.map((cat, index) => ({
      ...cat,
      displayOrder: index
    }));
    _dispatch({ type: 'SET_CATEGORIES', payload: updatedCategories });

    // Persister en base de données
    try {
      const { categoriesService } = await import('../../services/supabase/categories.service');
      await categoriesService.updateDisplayOrder(updates);
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde de l\'ordre des catégories:', error);
    }

    setDraggedCategoryId(null);
    setDragOverCategoryId(null);
  }, [draggedCategoryId, state.categories, _dispatch]);

  const handleCategoryDragEnd = useCallback(() => {
    setDraggedCategoryId(null);
    setDragOverCategoryId(null);
  }, []);

  // Handlers pour le drag & drop tactile (mobile)
  const handleCategoryTouchStart = useCallback((e: React.TouchEvent, categoryId: string) => {
    if (!isMobile) return;
    
    // Feedback haptique (vibration) si disponible
    if ('vibrate' in navigator) {
      navigator.vibrate(10); // Courte vibration de 10ms
    }
    
    // Empêcher le scroll pendant le drag
    const touch = e.touches[0];
    setDraggedCategoryId(categoryId);
    setIsDraggingCategory(true);
    
    logger.debug('Touch start sur catégorie', { categoryId, y: touch.clientY });
  }, [isMobile]);

  const handleCategoryTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isMobile || !isDraggingCategory || !draggedCategoryId) return;
    
    // Empêcher le scroll pendant le drag
    e.preventDefault();
    
    const touch = e.touches[0];
    
    // Trouver l'élément sous le doigt
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const categoryElement = element?.closest('[data-category-id]') as HTMLElement;
    
    if (categoryElement) {
      const targetCategoryId = categoryElement.dataset.categoryId;
      if (targetCategoryId && targetCategoryId !== draggedCategoryId) {
        // Feedback haptique léger quand on survole une nouvelle catégorie
        if (dragOverCategoryId !== targetCategoryId && 'vibrate' in navigator) {
          navigator.vibrate(5);
        }
        setDragOverCategoryId(targetCategoryId);
      } else if (targetCategoryId === draggedCategoryId) {
        setDragOverCategoryId(null);
      }
    } else {
      setDragOverCategoryId(null);
    }
  }, [isMobile, isDraggingCategory, draggedCategoryId, dragOverCategoryId]);

  const handleCategoryTouchEnd = useCallback(async (e: React.TouchEvent) => {
    if (!isMobile || !isDraggingCategory || !draggedCategoryId) return;
    
    const touch = e.changedTouches[0];
    
    // Trouver l'élément sous le doigt
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const categoryElement = element?.closest('[data-category-id]') as HTMLElement;
    
    if (categoryElement) {
      const targetCategoryId = categoryElement.dataset.categoryId;
      
      if (targetCategoryId && targetCategoryId !== draggedCategoryId) {
        // Feedback haptique de succès
        if ('vibrate' in navigator) {
          navigator.vibrate(20); // Vibration plus longue pour confirmer le drop
        }
        
        // Effectuer le drop
        const categories = [...state.categories];
        const draggedIndex = categories.findIndex(c => c.id === draggedCategoryId);
        const targetIndex = categories.findIndex(c => c.id === targetCategoryId);

        if (draggedIndex !== -1 && targetIndex !== -1) {
          // Réorganiser les catégories
          const [draggedCategory] = categories.splice(draggedIndex, 1);
          categories.splice(targetIndex, 0, draggedCategory);

          // Mettre à jour les displayOrder
          const updates = categories.map((cat, index) => ({
            id: cat.id,
            displayOrder: index
          }));

          // Mettre à jour l'état local immédiatement pour une UX fluide
          const updatedCategories = categories.map((cat, index) => ({
            ...cat,
            displayOrder: index
          }));
          _dispatch({ type: 'SET_CATEGORIES', payload: updatedCategories });

          // Persister en base de données
          try {
            await categoriesService.updateDisplayOrder(updates);
            logger.info('Ordre des catégories mis à jour (tactile)', { updates });
          } catch (error) {
            logger.error('Erreur lors de la sauvegarde de l\'ordre des catégories:', error);
          }
        }
      }
    }
    
    // Réinitialiser l'état
    setDraggedCategoryId(null);
    setDragOverCategoryId(null);
    setIsDraggingCategory(false);
  }, [isMobile, isDraggingCategory, draggedCategoryId, state.categories, _dispatch]);

  // Handlers pour le drag & drop tactile des markers
  const handleMarkerTouchStart = useCallback((e: React.TouchEvent, markerId: string) => {
    if (!isMobile) return;
    
    // Feedback haptique
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    
    setDraggedMarkerId(markerId);
    setIsDraggingMarker(true);
    
    logger.debug('Touch start sur marker', { markerId });
  }, [isMobile]);

  const handleMarkerTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isMobile || !isDraggingMarker || !draggedMarkerId) return;
    
    // Empêcher le scroll pendant le drag
    e.preventDefault();
    
    const touch = e.touches[0];
    
    // Trouver la catégorie sous le doigt
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const categoryElement = element?.closest('[data-category-id]') as HTMLElement;
    
    if (categoryElement) {
      const targetCategoryId = categoryElement.dataset.categoryId;
      if (targetCategoryId) {
        // Feedback haptique léger quand on survole une nouvelle catégorie
        if (dragOverCategoryId !== targetCategoryId && 'vibrate' in navigator) {
          navigator.vibrate(5);
        }
        setDragOverCategoryId(targetCategoryId);
      }
    } else {
      setDragOverCategoryId(null);
    }
  }, [isMobile, isDraggingMarker, draggedMarkerId, dragOverCategoryId]);

  const handleMarkerTouchEnd = useCallback(async (e: React.TouchEvent) => {
    if (!isMobile || !isDraggingMarker || !draggedMarkerId) return;
    
    const touch = e.changedTouches[0];
    
    // Trouver la catégorie sous le doigt
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const categoryElement = element?.closest('[data-category-id]') as HTMLElement;
    
    if (categoryElement) {
      const targetCategoryId = categoryElement.dataset.categoryId;
      
      if (targetCategoryId) {
        // Feedback haptique de succès
        if ('vibrate' in navigator) {
          navigator.vibrate(20);
        }
        
        // Effectuer le drop en utilisant le handler existant
        const marker = state.markers.find(m => m.id === draggedMarkerId);
        if (marker && marker.categoryId !== targetCategoryId) {
          // Créer un événement drag synthétique pour réutiliser la logique existante
          const syntheticEvent = {
            preventDefault: () => {},
            currentTarget: { style: { backgroundColor: '' } },
            dataTransfer: {
              getData: () => draggedMarkerId
            }
          } as any;
          
          await dragHandlers.handleDrop(syntheticEvent, targetCategoryId);
          logger.info('Marker déplacé via touch', { markerId: draggedMarkerId, targetCategoryId });
        }
      }
    }
    
    // Réinitialiser l'état
    setDraggedMarkerId(null);
    setDragOverCategoryId(null);
    setIsDraggingMarker(false);
  }, [isMobile, isDraggingMarker, draggedMarkerId, state.markers, dragHandlers]);

  const markersByCategory = useMemo(() => {
    // Utiliser un Map pour stocker les marqueurs uniques par catégorie
    const result: Record<string, Map<string, Marker>> = {};
    
    // Parcourir tous les marqueurs
    state.markers.forEach(marker => {
      if ('categoryId' in marker && typeof marker.categoryId === 'string') {
        // Initialiser la Map pour cette catégorie si elle n'existe pas
        if (!result[marker.categoryId]) {
          result[marker.categoryId] = new Map();
        }
        
        // Ajouter le marqueur à la Map en utilisant son ID comme clé
        // Cela garantit qu'un seul marqueur avec le même ID existe dans la catégorie
        result[marker.categoryId].set(marker.id, marker as Marker);
      } else {
        logger.warn('Marqueur trouvé sans categoryId valide', { marker });
      }
    });

    // Convertir les Maps en tableaux pour le rendu
    const finalResult: Record<string, Marker[]> = {};
    Object.keys(result).forEach(categoryId => {
      finalResult[categoryId] = Array.from(result[categoryId].values());
    });

    // Log supprimé pour améliorer les performances
    // Décommenter uniquement pour debug si nécessaire
    // logger.debug('État de markersByCategoryId:', {
    //   totalCategories: Object.keys(finalResult).length,
    //   markersPerCategory: Object.entries(finalResult).map(([catId, markers]) => ({
    //     categoryId: catId,
    //     markerCount: markers.length
    //   }))
    // });

    return finalResult;
  }, [state.markers]);

  return (
    <>
      <SidebarContainer isCollapsed={isCollapsed} className="sidebar-container">
        <SidebarContent isCollapsed={isCollapsed}>
          {/* Menus dépliants horizontaux générés dynamiquement */}
          <MenuContainer style={{ paddingTop: isMobile ? '70px' : '70px' }}>
            {/* Menu Catégories */}
            <MenuItem $isExpanded={expandedMenus.categories}>
              <MenuHeader 
                $isExpanded={expandedMenus.categories}
                onClick={() => toggleMenu('categories')}
              >
                <MenuTitle>
                  {MENU_CONFIG.categories.title}
                </MenuTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <AddMenuButton
                    $isVisible={isOwner}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddCategory();
                    }}
                    title="Ajouter une catégorie"
                  >
                    <FaPlus size={12} />
                  </AddMenuButton>
                  <MenuIcon $isExpanded={expandedMenus.categories}>
                    <FaChevronDown size={12} />
                  </MenuIcon>
                </div>
              </MenuHeader>
              <MenuContent $isExpanded={expandedMenus.categories}>
                <MenuInnerContent>
                  <CategoryList>
                    {categories.map(category => (
                  <MemoizedCategoryItem
                    key={category.id}
                    category={category}
                    markers={markersByCategory[category.id] || []}
                    isExpanded={expandedCategories.has(category.id)}
                    onToggle={toggleCategory}
                    onEdit={handleEditCategory}
                    onDelete={handleDeleteCategory}
                    dragHandlers={dragHandlers}
                    onMarkerClick={handleMarkerClick}
                    onCategoryDragStart={handleCategoryDragStart}
                    onCategoryDragOver={handleCategoryDragOver}
                    onCategoryDragLeave={handleCategoryDragLeave}
                    onCategoryDrop={handleCategoryDrop}
                    onCategoryDragEnd={handleCategoryDragEnd}
                    onCategoryTouchStart={handleCategoryTouchStart}
                    onCategoryTouchMove={handleCategoryTouchMove}
                    onCategoryTouchEnd={handleCategoryTouchEnd}
                    onMarkerTouchStart={handleMarkerTouchStart}
                    onMarkerTouchMove={handleMarkerTouchMove}
                    onMarkerTouchEnd={handleMarkerTouchEnd}
                    draggedCategoryId={draggedCategoryId}
                    isMobile={isMobile}
                  />
                    ))}
                  </CategoryList>
                </MenuInnerContent>
              </MenuContent>
            </MenuItem>

            {/* Menu Zones */}
            <MenuItem $isExpanded={expandedMenus.zones}>
              <MenuHeader 
                $isExpanded={expandedMenus.zones}
                onClick={() => toggleMenu('zones')}
              >
                <MenuTitle>
                  {MENU_CONFIG.zones.title}
                </MenuTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <AddMenuButton
                    $isVisible={isOwner}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddZone();
                    }}
                    title="Ajouter une zone"
                  >
                    <FaPlus size={12} />
                  </AddMenuButton>
                  <MenuIcon $isExpanded={expandedMenus.zones}>
                    <FaChevronDown size={12} />
                  </MenuIcon>
                </div>
              </MenuHeader>
              <MenuContent $isExpanded={expandedMenus.zones}>
                <MenuInnerContent>
                  <SectionErrorBoundary 
                    sectionName="Zones de prospection"
                    fallbackMessage="Impossible de charger les zones. Veuillez rafraîchir la page."
                  >
                    <ZonesSection
                      isCollapsed={false}
                      isOwner={isOwner}
                      onEditZone={(zone) => {
                        setEditingZone(zone);
                        setIsZoneModalOpen(true);
                      }}
                    />
                  </SectionErrorBoundary>
                </MenuInnerContent>
              </MenuContent>
            </MenuItem>

            {/* Espace pour ajouter de nouveaux menus à l'avenir */}
            {/* Exemple :
            <MenuItem $isExpanded={expandedMenus.reports}>
              <MenuHeader 
                $isExpanded={expandedMenus.reports}
                onClick={() => toggleMenu('reports')}
              >
                <MenuTitle>
                  {MENU_CONFIG.reports.title}
                </MenuTitle>
                <MenuIcon $isExpanded={expandedMenus.reports}>
                  <FaChevronDown size={12} />
                </MenuIcon>
              </MenuHeader>
              <MenuContent $isExpanded={expandedMenus.reports}>
                <MenuInnerContent>
                  <ReportsSection />
                </MenuInnerContent>
              </MenuContent>
            </MenuItem>
            */}
          </MenuContainer>
        </SidebarContent>
      </SidebarContainer>
      
      {/* Bouton de bascule de la sidebar */}
      <ToggleSidebarButton 
        isCollapsed={isCollapsed} 
        onClick={onToggleSidebar}
        aria-label={isCollapsed ? "Déplier la sidebar" : "Replier la sidebar"}
      >
        {isCollapsed ? <FaIcons.FaBars /> : <FaIcons.FaChevronLeft />}
      </ToggleSidebarButton>
      
      {isModalOpen && (
        <Modal
          isOpen={true}
          onClose={() => {
            setIsModalOpen(false);
            setEditingCategory(undefined);
          }}
        >
          <CategoryForm
            initialCategory={editingCategory}
            onSubmit={handleSubmitCategory}
            onClose={() => setIsModalOpen(false)}
          />
        </Modal>
      )}

      {/* Modal de zone en dehors de la sidebar pour overlay sur la carte */}
      {isZoneModalOpen && (
        <Modal
          isOpen={true}
          onClose={() => {
            setIsZoneModalOpen(false);
            setEditingZone(undefined);
            setDrawingCoordinates([]);
          }}
        >
          <ZoneForm
            coordinates={drawingCoordinates.length > 0 ? drawingCoordinates : editingZone?.coordinates}
            zone={editingZone}
            onSuccess={() => {
              console.log('✅ onSuccess appelé - Zone créée/modifiée avec succès');
              console.log('🔄 Émission RELOAD_ZONES pour mettre à jour la sidebar');
              setIsZoneModalOpen(false);
              setEditingZone(undefined);
              setDrawingCoordinates([]);
              // Émettre les événements de nettoyage et rechargement
              eventsService.emit('CANCEL_ZONE_DRAWING', {});
              eventsService.emit('RELOAD_ZONES', {});
            }}
            onCancel={() => {
              setIsZoneModalOpen(false);
              setEditingZone(undefined);
              setDrawingCoordinates([]);
            }}
          />
        </Modal>
      )}

      {/* Toast de notification pour le mode création de zone */}
      {showZoneToast && (
        <Toast
          message="Mode création de zone activé"
          duration={4000}
          onClose={() => setShowZoneToast(false)}
        />
      )}
    </>
  );
};

// Composant pour le bouton de bascule de la sidebar
export const SidebarToggleButton: React.FC<{
  isCollapsed: boolean;
  onClick: () => void;
}> = ({ isCollapsed, onClick }) => {
  return (
    <ToggleSidebarButton 
      isCollapsed={isCollapsed} 
      onClick={onClick}
      aria-label={isCollapsed ? "Déplier la sidebar" : "Replier la sidebar"}
    >
      {isCollapsed ? <FaIcons.FaBars /> : <FaIcons.FaChevronLeft />}
    </ToggleSidebarButton>
  );
};

export default Sidebar;

